import Vue from 'vue';
import eventBus from 'components/form/validator/eventBus';

/**
 * Intital state template
 * @return {{$valid: boolean, $invalid: boolean, $touched: boolean, $untouched: boolean, $dirty: boolean, $clean: boolean, $submitted: boolean, $error: boolean, $childForms: []}}
 */
const stateTemplate = () => ({
    $valid: true,
    $invalid: {
        get() {
            return !this.$valid;
        },
    },
    $touched: false,
    $untouched: {
        get() {
            return !this.$touched;
        },
    },
    $dirty: false,
    $clean: {
        get() {
            return !this.$dirty;
        },
    },
    $submitted: false,
    $error: false,
    $childForms: [],
});

/**
 * Checks and array of forms or fields and checks if all that states match the "shouldBe"
 *
 * @param {string} key
 * @param {Array<FieldControl|FormControl>} arr
 * @param {boolean} shouldBe
 *
 * @return {boolean}
 */
const isArrInOneState = (key, arr, shouldBe) => arr.reduce((value, item) => value !== shouldBe ? value : item.state[key], shouldBe);

/**
 * Form control to be bound to a component
 */
export default class FormControl {
    constructor(vm) {
        this._fields = [];
        this._vm = vm;
        this._state = stateTemplate();
        this._init = stateTemplate();
        this._forms = [];
        eventBus.trigger('validator.form.register', this);
        this._bindVmEvents();
    }

    get state() {
        return this._state;
    }

    /**
     * Submit and validate all fields
     */
    submit() {
        this._state.$submitted = true;
        this._fields.forEach(field => field.validate());
        this._updateDerivedStates();
    }

    /**
     * Add a field to the list
     * @param {FieldControl} field
     */
    addField(field) {
        this._fields.push(field);
        this._updateDerivedStates();
        Vue.set(this._vm.$form, field.name, field.state);
        return false;
    }

    /**
     * Remove a field from the list
     * @param {FieldControl} field
     */
    removeField(field) {
        this._fields.splice(this._fields.indexOf(field), 1);
        Vue.delete(this._vm.$form, field.name);
        delete this[field.name];
        this._updateDerivedStates();
        return true;
    }

    /**
     * Add child form
     * @param form
     */
    addForm(form) {
        if (form === this) {
            return true;
        }
        this._forms.push(form);
        this._state.$childForms.push(form.state);
        this._updateDerivedStates();
        return true;
    }

    /**
     * Add child form
     * @param form
     */
    removeForm(form) {
        const index = this._forms.indexOf(form);
        if (index === -1) {
            return true;
        }
        this._forms.splice(index, 1);
        this._state.$childForms.splice(index, 1);
        this._updateDerivedStates();
        return true;
    }


    /**
     * Determine the state of the from or a field.
     *
     * @param {string} state to determine
     * @param {string} [field] optional field to determine otherwise it's whole form start
     * @return {boolean|null}
     */
    determine(state, field) {
        if (!field) {
            this._updateDerivedStates();
            return this._state[state];
        }
        return (typeof this._state[field] !== 'undefined') ? this._state[field][state] : null;
    }


    /**
     * Listen for events on view model
     * @private
     */
    _bindVmEvents() {
        this._boundEvents = [
            eventBus.on('validator.field.register', this.addField.bind(this)),
            eventBus.on('validator.field.deregister', this.removeField.bind(this)),
            eventBus.on('validator.field.stateChange', this._handleFieldStateChange.bind(this)),
            eventBus.on('validator.form.register', this.addForm.bind(this)),
            eventBus.on('validator.form.deregister', this.removeForm.bind(this)),
        ];
    }

    remove() {
        eventBus.trigger('validator.form.deregister', this);
        this.unbindVmEvents();

    }

    /**
     * Unbind events
     */
    unbindVmEvents() {
        this._boundEvents.forEach(eventBus.off.bind(eventBus));
        this._boundEvents = [];
    }

    /**
     * When one of the children changes state.
     *
     * @param {string} key the updated state key
     * @private
     */
    _handleFieldStateChange(key) {
        // The form are not registering properly, this is a quick fix
        const updated = this._areAllFieldsOfState(key);
        if (updated === this._state[key]) return true;
        this._state[key] = updated;
        return true;
    }

    /**
     * Update all states derived by fields.
     *
     * @private
     */
    _updateDerivedStates() {
        ['$dirty', '$touched', '$valid'].forEach(this._handleFieldStateChange.bind(this));
    }

    /**
     * Update all stated
     * @param key
     * @return {*}
     * @private
     */
    _areAllFieldsOfState(key) {
        return isArrInOneState(key, this._fields, this._init[key]);
    }

    _areAllFormsOfState(key) {
        return this._forms.length === 0 ? this._init[key] : isArrInOneState(key, this._forms, this._init[key]);
    }
}



// WEBPACK FOOTER //
// ./src/components/form/validator/controls/FormControl.js